import { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { EditorState, Plugin, PluginKey, Transaction } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

import {
  BRACKET_FIELD_CLOSE,
  BRACKET_FIELD_OPEN,
  BracketFieldRange,
  findBracketFields,
} from "../utils/find-bracket-fields";

import classes from "./dot-phrase-fields.module.css";

const DOT_PHRASE_FIELDS_PLUGIN_NAME = "dotPhraseFields";

export interface DotPhraseField extends BracketFieldRange {
  placeholder: string;
}

interface DotPhraseFieldsState {
  fields: DotPhraseField[];
}

interface DotPhraseFieldsMeta {
  from: number;
  to: number;
}

const dotPhraseFieldsPluginKey = new PluginKey<DotPhraseFieldsState>(DOT_PHRASE_FIELDS_PLUGIN_NAME);

const NO_FIELDS: DotPhraseFieldsState = { fields: [] };

const inlineLeafPlaceholder = () => "\n";

function findDotPhraseFields(doc: ProseMirrorNode, from: number, to: number): DotPhraseField[] {
  const fields: DotPhraseField[] = [];

  doc.nodesBetween(from, to, (node, position) => {
    if (!node.isTextblock) return true;

    const blockStart = position + 1;
    const text = node.textBetween(0, node.content.size, undefined, inlineLeafPlaceholder);

    findBracketFields(text).forEach((field) =>
      fields.push({
        from: blockStart + field.from,
        to: blockStart + field.to,
        placeholder: text.slice(field.from, field.to),
      }),
    );

    return false;
  });

  return fields.filter((field) => field.from >= from && field.to <= to);
}

function isStillBracketed(doc: ProseMirrorNode, field: BracketFieldRange): boolean {
  const text = doc.textBetween(field.from, field.to);

  return (
    text.length >= 2 && text.startsWith(BRACKET_FIELD_OPEN) && text.endsWith(BRACKET_FIELD_CLOSE)
  );
}

function applyTransaction(tr: Transaction, previous: DotPhraseFieldsState): DotPhraseFieldsState {
  const meta = tr.getMeta(dotPhraseFieldsPluginKey) as DotPhraseFieldsMeta | undefined;

  if (meta) return { fields: findDotPhraseFields(tr.doc, meta.from, meta.to) };

  if (!tr.docChanged || previous.fields.length === 0) return previous;

  const fields = previous.fields
    .map((field) => ({
      ...field,
      from: tr.mapping.map(field.from, 1),
      to: tr.mapping.map(field.to, -1),
    }))
    .filter((field) => field.to > field.from && isStillBracketed(tr.doc, field));

  return fields.length === 0 ? NO_FIELDS : { fields };
}

export function startDotPhraseFields(tr: Transaction, from: number, to: number): Transaction {
  return tr.setMeta(dotPhraseFieldsPluginKey, { from, to } satisfies DotPhraseFieldsMeta);
}

export function readDotPhraseFields(state: EditorState): DotPhraseField[] {
  return dotPhraseFieldsPluginKey.getState(state)?.fields ?? NO_FIELDS.fields;
}

/**
 * Returns true when the text of `field` in `doc` differs from the placeholder the phrase
 * inserted, which means the provider changed it.
 */
export function isDotPhraseFieldChanged(doc: ProseMirrorNode, field: DotPhraseField): boolean {
  return (
    doc.textBetween(field.from, field.to, undefined, inlineLeafPlaceholder) !== field.placeholder
  );
}

export function buildDotPhraseFieldDecorations(state: EditorState): DecorationSet {
  return DecorationSet.create(
    state.doc,
    readDotPhraseFields(state).map((field) =>
      Decoration.inline(field.from, field.to, { class: classes.field }),
    ),
  );
}

export function buildDotPhraseFieldsPlugin(): Plugin<DotPhraseFieldsState> {
  return new Plugin<DotPhraseFieldsState>({
    key: dotPhraseFieldsPluginKey,

    state: {
      init: () => NO_FIELDS,
      apply: applyTransaction,
    },

    props: {
      decorations: buildDotPhraseFieldDecorations,
    },
  });
}
